
import moment from 'moment'
import I18n from './I18n'
import Printable from './Printable'
import RequestsCloudImages from './RequestsCloudImages'
import OrderStatus from '@/Models/Static/OrderStatus'
import FranceLog from '@/Models/Static/FiscalCountry/FranceLog'

export default {
  mixins: [
    I18n,
    Printable,
    RequestsCloudImages,
  ],

  data: () => ({
    OrderStatus,
    FranceLog,
    autoClosePreviewAfterPrint: false,
  }),

  methods: {
    fiscalBusiness(key) {
      return this.$store.getters['fiscalCountry/business'](key)
    },
    async _printTicketHeader (business) {
      if (business.logo) {
        AndroidPrinter.image(this.imgSrc(business.logo))
        AndroidPrinter.separator()
      }
      const legalName = this.fiscalBusiness('legalName')
      if (legalName) {
        AndroidPrinter.text(legalName, JSON.stringify({
          weight: 'bold',
        }))
      }
      if (business.slogan) {
        AndroidPrinter.text(business.slogan)
      }
      const nif = this.fiscalBusiness('nif')
      if (nif) {
        AndroidPrinter.text(nif)
      }
      const address = this.fiscalBusiness('address')
      if (address) {
        AndroidPrinter.text(address)
      }
      const postalCode = this.fiscalBusiness('postalCode')
      const city = this.fiscalBusiness('city')
      if (postalCode || city) {
        AndroidPrinter.text(`${postalCode} ${city}`)
      }
      await this.$store.dispatch('fiscalCountry/print', {
        target: 'AdditionalHeader',
        data: business,
      })
    },
    _printTicketTitle (invoice) {
      if (invoice === undefined) {
        AndroidPrinter.text(this.__('preInvoice'), JSON.stringify({ align: 'left' }))
        return
      }
      AndroidPrinter.text(this.invoiceType(invoice), JSON.stringify({ align: 'left' }))
      AndroidPrinter.text2(this.invoiceInfo(invoice), invoice.number)
    },
    invoiceType (invoice) {
      if (invoice.total.lessThan(0)) {
        return this.__(invoice.notAccountable ? 'refundedNotAccountable' : 'refundedInvoice')
          .formatUnicorn(this.$store.state.history.refunder.refunded?.number ?? '')
      }
      if (invoice.notAccountable) {
        return this.__('notAccountable')
      }
      if (!! invoice.customerJSON?.nif) {
        return this.__('invoice')
      }
      return this.__('simplifiedInvoice')
    },
    invoiceInfo (invoice) {
      return this.$store.getters['fiscalCountry']('invoiceInfo') ?? ''
    },
    _printTicketCustomer (customer) {
      if (! customer) {
        return
      }
      if (customer.name) {
        const name = customer.last_name ? `${customer.name} ${customer.last_name}` : customer.name
        AndroidPrinter.text(name, JSON.stringify({ align: 'left' }))
      }
      if (customer.nif) {
        AndroidPrinter.text(customer.nif, JSON.stringify({ align: 'left' }))
      }
      if (customer.address) {
        AndroidPrinter.text(customer.address, JSON.stringify({ align: 'left' }))
      }
      if (customer.city) {
        AndroidPrinter.text(customer.postalCode
          ? `${customer.postalCode} ${customer.city}`
          : customer.city, JSON.stringify({ align: 'left' }))
      }
      if (customer.state && customer.country) {
        AndroidPrinter.text(`(${customer.state}) - ${customer.country}`, JSON.stringify({ align: 'left' }))
      }
    },
    _printTicketInfo (order, invoice) {
      const date = invoice === undefined ? moment() : invoice.dateMoment
      AndroidPrinter.text2(date.format('LLL'), `# ${order.id}`)
      AndroidPrinter.text2(order.tableName, `[${order.guests}]`)

      const additional = this.$store.getters['fiscalCountry/get']('ticketInfo') ?? [
        this.__('waiter') + ': ' + this.$store.getters['rummage/waiter'](order.tenantUser_id)
      ]
      additional.forEach(line => {
        AndroidPrinter.text(line, JSON.stringify({ align: 'left' }))
      })
    },
    formatPrice (v) {
      return Number(v || 0).toFixed(2)
    },
    async printTicket (order, invoice) {
      const business = this.$store.getters['rummage/business']() || {}

      this.printing = true
      this.initPrinter()

      await this.$store.dispatch('fiscalCountry/print', {
        target: 'PreHeader',
        data: { order, invoice },
      })
      AndroidPrinter.separator()
      await this._printTicketHeader(business)
      AndroidPrinter.separator()
      this._printTicketTitle(invoice)
      this._printTicketCustomer(invoice?.customerJSON)
      AndroidPrinter.separator()
      this._printTicketInfo(order, invoice)
      AndroidPrinter.separator('-')

      const contents = invoice?.orderContents ?? order.orderContents
      contents.forEach(content => {
        AndroidPrinter.text3(
          `${content.quantity} ${content.name}`,
          this.$store.getters['fiscalCountry/contentPrice']({ content, invoice }) ?? this.formatPrice(content.itemPrice),
          this.formatPrice(content.itemPrice * content.quantity))
        if (content.orderContentCombination !== null) {
          AndroidPrinter.text3(`  - ${content.orderContentCombination.itemName}`, this.formatPrice(content.orderContentCombination.price), this.formatPrice(content.orderContentCombination.price * content.quantity))
        }
        content.modifiers.forEach(modifier => {
          AndroidPrinter.text3(`  - ${modifier.name}`, this.formatPrice(modifier.price), this.formatPrice(modifier.price * content.quantity))
        })
        content.getFormattedSubContents(false).forEach(subContent => {
          AndroidPrinter.text3(`  * ${subContent.quantity} ${subContent.itemName}`, this.formatPrice(subContent.itemPrice), this.formatPrice(subContent.itemPrice * subContent.quantity))
          subContent.modifiers?.filter(subContentModifier => subContentModifier.price.notEqualTo(0)).forEach(subContentModifier => {
            AndroidPrinter.text3(`    - ${subContentModifier.name}`, this.formatPrice(subContentModifier.price), this.formatPrice(subContentModifier.price * subContent.quantity))
          })
        })
      })
      AndroidPrinter.separator('=')

      await this._printTicketSummary(order, invoice)

      if (business.web) {
        AndroidPrinter.separator('-')
        AndroidPrinter.separator()
        AndroidPrinter.text(business.web)
      }

      await this.$store.dispatch('fiscalCountry/print', {
        target: 'AdditionalFooter',
        data: business,
      })

      return Bridge.interfaces.AndroidPrinter.print()
    },
    async _printTicketSummary (order, invoice) {
      if (! invoice) {
        return this.$store.dispatch('fiscalCountry/print', {
          target: 'CheckSummary',
          data: order,
        })
      }

      this.$store.commit('orders/invoiceSummary', invoice)

      await this.$store.dispatch('fiscalCountry/print', {
        target: 'InvoiceSummary',
        data: { order, invoice },
      })
      if (invoice.accountable) {
        this._printPaymentMethods(invoice.orderPayments)
      }
    },
    _printPaymentMethods (payments) {
      if (! payments?.length) {
        return
      }
      AndroidPrinter.separator('-')
      AndroidPrinter.text(this.__('paymentMethod'))
      Object.values(payments.groupBy('paymentMethod'))
        .forEach(payments => {
          let name = this.$store.getters['rummage/paymentMethod'](payments[0].paymentMethod)?.name || '--'
          const amount = payments.sum('payAmount')
          if (amount.lessThan(0)) {
            name += `-${this.__('refund')}`
          }
          AndroidPrinter.text(`${name}: ${amount.toFixed(2)}`)
        })
    },
    printInvoice (invoice) {
      const order = this.$store.state.orders.open
        .find(order => order.id == invoice?.order_id)
      if (! order) {
        throw new Error('Order not found')
      }
      return this.printTicket(order, invoice)
        .then(() => {
          this.$log('Printed invoice ticket', 'Order', invoice.order_id)
          this.printing = false
        })
        .catch(() => {
          this.$log('Print error', 'Order', invoice.order_id)
          this.$logFrance(this.FranceLog.PRINTER_NOT_AVAILABLE, `Invoice ${invoice.id}`)
          this.printing = false
          this.$store.dispatch('alert/showError', this.__('printError'))
        })
        // The Promise returned by the Bridge cannot use the finally method!
    },
    printCheck (order) {
      if (order.status == OrderStatus.CHECK_PRINTED && this.$store.getters['fiscalCountry/shouldAddReason']) {
        this.printReasonDialog = true
        return
      }
      return this.signPrintCheck(order)
    },
    async signPrintCheck (order, reason = null) {
      await this.$store.dispatch('fiscalCountry/signPrintCheck', { order, reason })
      return this.printTicket(order)
        .then(() => {
          this.$log('Printed order ticket', 'Order', order.id)
          this._updateOrderStatus(order, this.OrderStatus.CHECK_PRINTED)
        })
        .catch(() => {
          this.$log('Print error', 'Order', order.id)
          this.$logFrance(this.FranceLog.PRINTER_NOT_AVAILABLE, `Order ${order.id}`)
          this.$store.dispatch('alert/showError', this.__('printError'))
          this.printing = false
        })
        // The Promise returned by the Bridge cannot use the finally method!
    },
    _updateOrderStatus (order, status) {
      if (order.status == status) {
        this.printing = false
        return
      }
      this.$store.commit('orders/changeStatus', status)
      this.$api(['updateStatus', 'Order'], {
        order_id: order.id,
        status,
      })
        .then(() => {
          this.$log('Updated order status to CHECK_PRINTED', 'Order', order.id)
          if (this.autoClosePreviewAfterPrint) {
            this.$emit('close')
            this.$store.dispatch('sync/orders')
          }
        })
        .finally(() => this.printing = false)
    },
    // printMessage (message) {
    //   this.printMessages([message])
    // },
    // printMessages (messages) {
    //   this.printing = true
    //   this.initPrinter()

    //   AndroidPrinter.separator()
    //   messages.forEach(message => {
    //     AndroidPrinter.text(message)
    //     AndroidPrinter.separator()
    //   })

    //   Bridge.interfaces.AndroidPrinter.print()
    //     .then(() => this.$log(`Printed: ${messages.join(' - ')}`))
    //     .catch(() => this.$store.dispatch('alert/showError', this.__('printError')))

    //   this.printing = false
    // },
  },
}
